import React, { FC, useEffect, useState } from 'react'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  AISlotModel,
  fetchAiSlotById,
  selectAiSlotById,
  selectIsAiSlotIdInProgress,
} from '@store/slices/aiSlot.slice'
import { toDateHourString, toHourMinString } from '@shared/tools/time.tools'
import AppAudioPlayerComponent from '@components/shared/appAudioPlayer.component'
import { Accordion } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import AppButton from '@components/shared/appButton'
import { Trash } from 'react-bootstrap-icons'
import SlotStatusComponent from '@components/slot/slotStatus.component'
import {
  fetchAllRegions,
  selectAllRegions,
  selectRegionById,
  selectRegionLoading,
} from '@store/slices/region.slice'
import {
  deleteNarration,
  fetchAllNarrations,
  NarrationModel,
  selectNarrationLoading,
  selectNarrationsByIds,
} from '@store/slices/narration.slice'
import { DjangoFilter } from '@store/slices/generic.slice'
import ConsentModal from '@components/shared/consentModal.component'

const SlotAudioComponent: FC<{ slotId: EntityId }> = ({ slotId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const aiSlot: AISlotModel | undefined = useSelector((state: RootState) =>
    selectAiSlotById(state, slotId)
  )
  const slotLoading = useSelector((state: RootState) =>
    selectIsAiSlotIdInProgress(state, slotId)
  )
  const narrationsLoading = useSelector(selectNarrationLoading)
  const slotNarrations: NarrationModel[] = useSelector((state: RootState) =>
    aiSlot ? selectNarrationsByIds(state, aiSlot.narrations) : []
  )

  useEffect(() => {
    if (!aiSlot && !slotLoading) {
      dispatch(fetchAiSlotById(slotId))
    }
  }, [])

  useEffect(() => {
    if (
      aiSlot?.narrations &&
      aiSlot?.narrations.length != 0 &&
      (!slotNarrations || slotNarrations.length == 0) &&
      !narrationsLoading
    ) {
      fetchNarrationsForSlot()
    }
  }, [slotNarrations, aiSlot?.narrations])

  const fetchNarrationsForSlot = () => {
    const filter = new DjangoFilter<AISlotModel>()
    aiSlot.narrations && filter.addIdFilter(aiSlot.narrations)
    dispatch(fetchAllNarrations(filter))
  }

  return (
    <div className="my-2">
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="d-flex gap-2 align-items-center">
              <h4 className={'p-0 m-0'}>
                {aiSlot && toHourMinString(aiSlot.exactTime)}
              </h4>
              <SlotStatusComponent aiSlotId={slotId}></SlotStatusComponent>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            {aiSlot &&
              slotNarrations &&
              slotNarrations.map((narration) => (
                <NarrationAudio
                  key={aiSlot.id.toString() + narration.region.toString()}
                  narration={narration}
                  slotId={slotId}
                />
              ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}

export default SlotAudioComponent

const NarrationAudio: FC<{ narration: NarrationModel; slotId: EntityId }> = ({
  narration,
  slotId,
}) => {
  const { playlistId } = useParams()
  const regionsLoading = useSelector(selectRegionLoading)
  const regions = useSelector(selectAllRegions)
  const region = useSelector((state: RootState) =>
    narration.region ? selectRegionById(state, narration.region) : null
  )
  const [showConsentModal, setShowConsentModal] = useState<boolean>(false)
  useEffect(() => {
    if (regions?.length == 0 && !regionsLoading) dispatch(fetchAllRegions())
  }, [regions])
  const dispatch = useDispatch<AppDispatch>()
  const slotLoading = useSelector((state: RootState) =>
    selectIsAiSlotIdInProgress(state, slotId)
  )

  const onDeleteAudioClick = () => {
    setShowConsentModal(true)
  }

  const dispatchDeleteNarration = () => {
    setShowConsentModal(false)
    narration.id &&
      dispatch(deleteNarration(narration.id))
        .then(unwrapResult)
        .then(() => slotId && fetchAiSlotById(slotId))
  }

  const isNarrationUpdated =
    narration &&
    narration.audioCreated &&
    (!narration.uploaded ||
      (narration.uploaded &&
        new Date(narration.uploaded) < new Date(narration.audioCreated)))
  return (
    <div className="d-flex gap-2 justify-content-between align-items-center my-1">
      <ConsentModal
        show={showConsentModal}
        header={'Smazání regionu'}
        body={`Opravdu chcete smazat region ${region?.name}?`}
        onAgree={dispatchDeleteNarration}
        onBack={() => setShowConsentModal(false)}
        onHide={() => setShowConsentModal(false)}
      />
      <div className={'d-flex gap-2 align-items-center'}>
        <AppButton
          isLoading={slotLoading}
          buttonVariant={'danger'}
          tooltip={'Smazat region'}
          buttonChild={<Trash />}
          onClick={onDeleteAudioClick}
        ></AppButton>
        <Link
          to={`/playlist/${playlistId}/slot/${slotId}/region/${narration.region}`}
        >
          {region?.name}
        </Link>
      </div>
      <div className={'d-flex flex-column text-end'}>
        {narration?.audioCreated && (
          <span>
            Naposledy aktualizováno:{' '}
            <strong className={isNarrationUpdated ? 'text-primary' : ''}>
              {toDateHourString(narration.audioCreated)}
            </strong>
          </span>
        )}
        {narration?.uploaded && (
          <span>
            Naposledy publikováno:{' '}
            <strong className={isNarrationUpdated ? '' : 'text-success'}>
              {toDateHourString(narration.uploaded)}
            </strong>
          </span>
        )}
      </div>
      <div className={'w-50'}>
        <AppAudioPlayerComponent
          audioSrc={narration?.audioUrl}
        ></AppAudioPlayerComponent>
      </div>
    </div>
  )
}
